import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close, Refresh, Search } from "@mui/icons-material";

interface RfqListToolbarProps {
  searchKeyword: string;
  onSearchChange: (value: string) => void;
  onCheckEmails: () => void;
  isRefreshing: boolean;
  lastCheckedTime: string | null;
  /** A background refetch is running (not the first load). */
  isUpdating: boolean;
  isRealtimeConnected: boolean;
}

/** Search, email check, refetch and real-time sync status, above the RFQ table. */
export default function RfqListToolbar({
  searchKeyword,
  onSearchChange,
  onCheckEmails,
  isRefreshing,
  lastCheckedTime,
  isUpdating,
  isRealtimeConnected,
}: RfqListToolbarProps) {
  return (
    <Box
      sx={{
        mt: 2,
        mb: 2.5,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        columnGap: 1.5,
        rowGap: 1,
      }}
    >
      <TextField
        size="small"
        placeholder="Search by item name or model number..."
        value={searchKeyword}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onSearchChange(event.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: searchKeyword && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => onSearchChange("")}
                edge="end"
                aria-label="Clear search"
              >
                <Close fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ flex: "1 1 280px", maxWidth: { md: 520, xl: 640 } }}
      />
      <Tooltip title="Check for new emails">
        <span>
          <Button
            variant="outlined"
            onClick={onCheckEmails}
            disabled={isRefreshing}
            startIcon={isRefreshing ? <CircularProgress size={16} /> : <Refresh />}
            sx={{ height: 40, whiteSpace: "nowrap" }}
          >
            Check emails
          </Button>
        </span>
      </Tooltip>
      {lastCheckedTime && (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
          Last checked: {lastCheckedTime}
        </Typography>
      )}
      {isUpdating && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Updating...
          </Typography>
        </Box>
      )}
      {/* Real-time sync status indicator */}
      <Tooltip title={isRealtimeConnected ? "Real-time sync active - changes by other users will appear automatically" : "Connecting to real-time sync..."}>
        <Box
          sx={{
            ml: "auto",
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.25,
            py: 0.5,
            borderRadius: 999,
            border: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: isRealtimeConnected ? "success.main" : "warning.main",
              animation: isRealtimeConnected ? "none" : "pulse 1.5s infinite",
              "@keyframes pulse": {
                "0%": { opacity: 1 },
                "50%": { opacity: 0.4 },
                "100%": { opacity: 1 },
              },
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap", fontSize: "0.8125rem" }}>
            {isRealtimeConnected ? "Live" : "Connecting..."}
          </Typography>
        </Box>
      </Tooltip>
    </Box>
  );
}
